/**
 * CihStatStrip — horizontal 3-or-4-cell stat strip with vertical rules.
 *
 * Rendered on both light (default) and dark surfaces. Consumers pre-compute
 * the cells with `buildStatStrip(...)`.
 *
 * Lays out two columns on phones and one row from `md` up. A single row of
 * four leaves roughly 54px of content box at 375px, which is narrower than a
 * stat value like an originator's name — the value then spills across the rule
 * into its neighbour.
 *
 * Aliases: stat strip, stats row, stat bar
 */

import { cn } from "~/lib/cn";
import type { StatCell } from "~/lib/shared/cih/stat-strip";

type Props = {
  readonly cells: ReadonlyArray<StatCell>;
  readonly variant?: "light" | "dark";
};

/**
 * Tailwind reads class names as literals, so the handful of counts
 * `buildStatStrip` can produce are spelled out rather than interpolated.
 */
const MD_COLUMN_CLASS: ReadonlyMap<number, string> = new Map([
  [1, "md:grid-cols-1"],
  [2, "md:grid-cols-2"],
  [3, "md:grid-cols-3"],
  [4, "md:grid-cols-4"],
]);

export function CihStatStrip({ cells, variant = "light" }: Props) {
  if (cells.length === 0) return null;

  const isDark = variant === "dark";
  const borderColor = isDark ? "border-purple-900/30" : "border-gray-200";

  return (
    <div
      className={cn(
        "grid border-y",
        cells.length === 1 ? "grid-cols-1" : "grid-cols-2",
        MD_COLUMN_CLASS.get(cells.length) ?? "md:grid-cols-4",
        isDark
          ? "border-purple-900/40 bg-transparent"
          : "border-gray-200 bg-white",
      )}
    >
      {cells.map((cell, i) => {
        const isLast = i === cells.length - 1;
        // Phone layout: two columns, so a cell has a right-hand neighbour when
        // its index is even, and a row above it from the third cell on.
        const inLeftColumn = i % 2 === 0;
        const inFirstRow = i < 2;

        return (
          <div
            key={`${cell.label}-${i}`}
            className={cn(
              "px-5 py-5 md:px-10 md:py-6",
              inLeftColumn && !isLast && "border-r",
              !inFirstRow && "border-t md:border-t-0",
              isLast ? "md:border-r-0" : "md:border-r",
              borderColor,
            )}
          >
            <div
              className={cn(
                "font-heading text-3xl leading-none font-medium tracking-tight break-words tabular-nums md:text-4xl",
                isDark ? "text-white" : "text-gray-900",
              )}
            >
              {cell.value}
            </div>
            <div
              className={cn(
                "mt-1.5 text-[10px] font-medium tracking-[0.22em] uppercase",
                isDark ? "text-purple-300/70" : "text-gray-500",
              )}
            >
              {cell.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
